import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { PatientService } from '../../../../services/patient.service'
import {
  RPMTimeEntry,
  RPM_ACTIVITY_DISPLAY,
  formatMinutes,
  formatSeconds,
} from '../../../../model/rpm.types'
import { toast } from 'react-toastify'
import Helper from '../../../../utils/helper'
import moment from 'moment'

interface IProps {
  patientId?: number
  rpmEnrollmentId?: number
  billingMonth?: string
  onEntryDeleted?: () => void
  onEntryEdited?: () => void
}

const patientService = new PatientService()

const RPMTimeLogComponent: React.FC<IProps> = ({
  patientId,
  rpmEnrollmentId,
  billingMonth,
  onEntryDeleted,
  onEntryEdited,
}) => {
  const [entries, setEntries] = useState<RPMTimeEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [entryToDelete, setEntryToDelete] = useState<RPMTimeEntry | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Get current billing month if not provided
  const getCurrentBillingMonth = () => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
  }

  const loadEntries = useCallback(() => {
    if (!patientId) return

    setLoading(true)
    const month = billingMonth || getCurrentBillingMonth()

    patientService
      .getRPMTimeEntries(patientId, {
        billing_month: month,
        rpm_enrollment_id: rpmEnrollmentId,
      })
      .then((res) => {
        if (res?.data?.success) {
          setEntries(res.data.time_entries || [])
        }
      })
      .catch((err) => {
        console.error('Failed to load time entries:', err)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [patientId, rpmEnrollmentId, billingMonth])

  useEffect(() => {
    loadEntries()
  }, [loadEntries])

  const handleDeleteClick = (entry: RPMTimeEntry) => {
    setEntryToDelete(entry)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!entryToDelete) return

    setDeleting(true)
    try {
      const res = await patientService.deleteRPMTimeEntry(entryToDelete.id)
      if (res?.data?.success) {
        toast.success('Time entry deleted', Helper.bottom_center())
        loadEntries()
        onEntryDeleted?.()
      }
    } catch (err) {
      console.error('Failed to delete time entry:', err)
      toast.error('Failed to delete time entry', Helper.bottom_center())
    } finally {
      setDeleting(false)
      setDeleteDialogOpen(false)
      setEntryToDelete(null)
    }
  }

  // Group entries by staff
  const entriesByStaff = entries.reduce((acc, entry) => {
    const staff = entry.staff_name || entry.staff_id
    if (!acc[staff]) {
      acc[staff] = []
    }
    acc[staff].push(entry)
    return acc
  }, {} as Record<string, RPMTimeEntry[]>)

  // Calculate totals (use seconds if available, otherwise fall back to minutes)
  const totalSeconds = entries.reduce((sum, e) => sum + (e.duration_seconds || (e.duration_minutes || 0) * 60), 0)
  const totalMinutes = Math.floor(totalSeconds / 60)

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={20} />
      </Box>
    )
  }

  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" py={1}>
        No time entries for this month
      </Typography>
    )
  }

  return (
    <>
      <Box>
        {/* Summary */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="body2" color="text.secondary">
            {entries.length} entries by {Object.keys(entriesByStaff).length} staff
          </Typography>
          <Stack direction="row" alignItems="baseline" spacing={0.5}>
            <Typography variant="body2" fontWeight="medium" fontFamily="monospace">
              {formatSeconds(totalSeconds)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ({totalMinutes} min billable)
            </Typography>
          </Stack>
        </Stack>

        {/* Entries table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Activity</TableCell>
                <TableCell>Staff</TableCell>
                <TableCell align="right">Duration</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id} hover>
                  <TableCell>
                    {moment(entry.activity_date).format('MMM D')}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">
                        {RPM_ACTIVITY_DISPLAY[entry.activity_type] || entry.activity_type}
                      </Typography>
                      {entry.is_auto_tracked && (
                        <Chip
                          label="Auto"
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 18 }}
                        />
                      )}
                    </Stack>
                    {entry.notes && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {entry.notes}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {entry.staff_name || entry.staff_id}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium" fontFamily="monospace">
                      {formatSeconds(entry.duration_seconds || (entry.duration_minutes || 0) * 60)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(entry)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Staff breakdown */}
        {Object.keys(entriesByStaff).length > 1 && (
          <Box mt={2}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              By Staff:
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              {Object.entries(entriesByStaff).map(([staff, staffEntries]) => {
                const staffSeconds = staffEntries.reduce((s, e) => s + (e.duration_seconds || (e.duration_minutes || 0) * 60), 0)
                return (
                  <Typography key={staff} variant="caption" fontFamily="monospace">
                    {staff}: {formatSeconds(staffSeconds)}
                  </Typography>
                )
              })}
            </Stack>
          </Box>
        )}
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Time Entry?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this time entry?
          </Typography>
          {entryToDelete && (
            <Box mt={1} p={1} sx={{ bgcolor: 'var(--color-background-subtle)', borderRadius: 1 }}>
              <Typography variant="body2">
                {moment(entryToDelete.activity_date).format('MMM D, YYYY')} -{' '}
                {RPM_ACTIVITY_DISPLAY[entryToDelete.activity_type]} -{' '}
                {formatSeconds(entryToDelete.duration_seconds || (entryToDelete.duration_minutes || 0) * 60)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default RPMTimeLogComponent
